import { useState, useCallback, useEffect } from 'react';
import { fetchRoads } from '../services/api';

/**
 * Custom hook for managing road color overlay functionality
 */
export const useRoadColors = (mapRef) => {
  const [showRoadColors, setShowRoadColors] = useState(false);

  // Fetch and display road colors
  const updateRoadColors = useCallback(async () => {
    if (!mapRef.current) return;
    
    const map = mapRef.current;
    
    // Check if map is loaded and valid
    if (!map || !map.getStyle || !map.getStyle()) return;
    
    try {
      const bounds = map.getBounds();
      const zoom = Math.round(map.getZoom());
      const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
      
      const geojson = await fetchRoads(bbox, zoom);
      
      // Update or create the source
      if (map.getSource && map.getSource('road-colors')) {
        map.getSource('road-colors').setData(geojson);
      } else {
        map.addSource('road-colors', {
          type: 'geojson',
          data: geojson
        });
        
        // Add the layer
        map.addLayer({
          id: 'road-color-overlay',
          type: 'line',
          source: 'road-colors',
          paint: {
            'line-color': ['get', 'color'],
            'line-width': [
              'interpolate',
              ['exponential', 1.5],
              ['zoom'],
              10, 2,
              14, 4,
              18, 10
            ],
            'line-opacity': 0.8
          }
        });
      }
    } catch (error) {
      // Error fetching road colors
    }
  }, [mapRef]);

  // Effect to handle road color layer visibility
  useEffect(() => {
    if (!mapRef.current) return;
    
    const map = mapRef.current;
    
    // Check if map is loaded
    if (!map || !map.getStyle || !map.getStyle()) return;
    
    // Add or remove road color layer based on toggle
    if (showRoadColors) {
      updateRoadColors();
      
      // Update on map move
      const handleMoveEnd = () => {
        if (showRoadColors && map && map.getStyle && map.getStyle()) {
          updateRoadColors();
        }
      };
      map.on('moveend', handleMoveEnd);
      
      return () => {
        if (map) {
          map.off('moveend', handleMoveEnd);
        }
      };
    } else {
      // Remove layer and source when disabled
      try {
        if (map.getLayer && map.getLayer('road-color-overlay')) {
          map.removeLayer('road-color-overlay');
        }
        if (map.getSource && map.getSource('road-colors')) {
          map.removeSource('road-colors');
        }
      } catch (error) {
        // Error removing road color layer/source
      }
    }
  }, [showRoadColors, updateRoadColors, mapRef]);

  return {
    showRoadColors,
    setShowRoadColors,
    updateRoadColors,
  };
};

